import { Folder } from "@/types/folders";
import { Entity, EntityType, getEntityNumericId } from "@/queries/folder-entities/types";

export const formatFoldersWithSubFolders = (folders: Folder[]) =>
  folders?.reduce((acc: Folder[], folder: Folder) => {
    const subFolders = folders.filter(f => f.parent_id === folder.id);
    const newFolder = {
      ...folder,
      subFolders: subFolders,
    };

    acc.push(newFolder);

    return acc;
  }, []) ?? [];

/**
 * Build breadcrumb path from root to the given folder
 */
export const buildBreadcrumbPath = (
  folderId: number | null,
  folders: Folder[],
): { name: string; id: number; parent_id: number | null }[] => {
  if (!folderId) {
    return [];
  }

  const breadcrumbs: {
    name: string;
    id: number;
    parent_id: number | null;
  }[] = [];
  let currentId: number | null = folderId;

  while (currentId !== null) {
    const currentIdCopy = currentId as number;
    const folder = folders.find(f => f.id === currentIdCopy);
    if (!folder) {
      break;
    }

    breadcrumbs.unshift({
      name: folder.name,
      id: folder.id,
      parent_id: folder.parent_id,
    });

    currentId = folder.parent_id;
  }

  return breadcrumbs;
};

/**
 * Get all descendant folder IDs for a given folder
 */
export const getFolderDescendants = (folderId: number, folders: Folder[]): number[] => {
  const descendants: number[] = [];
  const directChildren = folders.filter(f => f.parent_id === folderId);

  for (const child of directChildren) {
    descendants.push(child.id);
    descendants.push(...getFolderDescendants(child.id, folders));
  }

  return descendants;
};

/**
 * Check if a folder can be moved to a target folder without creating cycles
 */
export const canMoveFolderTo = (
  folderEntity: Entity,
  targetFolderId: number | null,
  folders: Folder[],
): boolean => {
  // Can't move to itself
  if (folderEntity.id === targetFolderId) {
    return false;
  }

  // If moving to root (null), always allowed
  if (targetFolderId === null) {
    return true;
  }

  // Get all descendants of the folder being moved
  const folderId = getEntityNumericId(folderEntity.id);

  if (folderId === undefined) {
    return false;
  }

  const descendants = getFolderDescendants(folderId, folders);

  // Can't move to any of its descendants
  return !descendants.includes(targetFolderId);
};

/**
 * Filter out folders that would create cycles when moving the given entities
 */
export const getAvailableFolders = (entities: Entity[], folders: Folder[]): Folder[] => {
  const folderEntities = entities.filter(entity => entity.type === EntityType.FOLDER);

  // If no folder entities are being moved, all folders are available
  if (folderEntities.length === 0) {
    return folders;
  }

  // Filter out folders that would create cycles
  return folders.filter(folder =>
    folderEntities.every(folderEntity => canMoveFolderTo(folderEntity, folder.id, folders)),
  );
};
